import type { JSX, ReactNode } from 'react'
import { Badge } from '~/components/ui/badge'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/card'
import { cn } from '~/lib/utils'
import type { PermissionTuple } from '~/utils/ability'
import type { RotationStatus } from '~/lib/product-rotations-query'
import { ROTATION_STATUS_LABELS, type RotationRoleKey } from './rotation-model'

/** Écrans Rotations : la lecture des produits ouvre la liste et les fiches (spec §7). */
export const ROTATIONS_READ: PermissionTuple = ['read', 'products.products.product']
/** Création, modification et suppression d'une rotation. */
export const ROTATIONS_EDIT: PermissionTuple = ['edit', 'products.products.product']

const STATUS_CLASSES: Record<RotationStatus, string> = {
  upcoming: 'bg-secondary-blue-100 text-secondary-blue-600',
  current: 'bg-secondary-orange-100 text-secondary-orange-800',
  done: 'bg-sage-100 text-sage-600',
}

export function RotationStatusBadge({
  status,
  children,
}: {
  status: RotationStatus
  children?: ReactNode
}): JSX.Element {
  return (
    <Badge className={cn('rounded-md font-semibold', STATUS_CLASSES[status])}>
      {children ?? ROTATION_STATUS_LABELS[status]}
    </Badge>
  )
}

/** Pastille de compte : sortants en rouge, entrants en vert (maquette, écrans 1 et 2). */
export function RoleCountBadge({
  role,
  children,
}: {
  role: RotationRoleKey
  children: ReactNode
}): JSX.Element {
  return (
    <Badge
      className={cn(
        'rounded-md font-semibold tabular-nums',
        role === 'outgoing'
          ? 'bg-secondary-red-100 text-destructive'
          : 'bg-primary-50 text-primary-700',
      )}
    >
      {children}
    </Badge>
  )
}

/** Carte des sections de l'écran (et de leur squelette) : sans marge interne, coins `2xl`. */
export const ROTATION_CARD_CLASS = 'gap-0 rounded-2xl p-0'
/** En-tête séparé du contenu par un filet (le `pb-6` d'un en-tête à filet ramené à `py-3`). */
export const ROTATION_CARD_HEADER_CLASS = 'gap-x-4 border-b px-4 py-3 [.border-b]:pb-3'

/** Carte d'une section du formulaire : titre, description, action à droite, contenu. */
export function RotationSection({
  title,
  description,
  action,
  children,
  className,
  bodyClassName,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  children?: ReactNode
  className?: string
  bodyClassName?: string
}): JSX.Element {
  return (
    <Card className={cn(ROTATION_CARD_CLASS, className)}>
      <CardHeader className={ROTATION_CARD_HEADER_CLASS}>
        <CardTitle role="heading" aria-level={2} className="font-semibold text-primary-800">
          {title}
        </CardTitle>
        {description && <CardDescription className="text-sm">{description}</CardDescription>}
        {action && <CardAction className="flex shrink-0 items-center gap-2">{action}</CardAction>}
      </CardHeader>
      {children !== undefined && <CardContent className={bodyClassName}>{children}</CardContent>}
    </Card>
  )
}
